import { createServerFn } from "@tanstack/react-start";
import { shortLinks } from "@wags/db/schema";
import type { BoundaryUnknown } from "@wags/utils/boundary-unknown.ts";
import { eq } from "drizzle-orm";
import type IShortLinkRedirectEventFetcher from "./short-link-redirect-event-fetcher.interface";
import {
    createShortLinkDestination,
    SHORT_LINK_PRIVATE_NO_STORE_HEADERS,
    shortLinkDestinationAnalyticsContext,
    type ShortLinkRequestAttribution,
    type ShortLinkUnattributedReason,
} from "./short-link-attribution";
import {
    postHogDistinctIdCookieSchema,
    resolveShortLinkInputSchema,
    type ShortLinkRedirectEvent,
    type ShortLinkRedirectEventProperties,
} from "./resolve-short-link.contract";

const POSTHOG_API_HOST = "https://us.i.posthog.com";
const REQUEST_ONLY_DISTINCT_ID = "short_link_redirect_request";

export type ShortLinkResolution =
    | {
          kind: "bridged";
          destinationHref: string;
          redirectRequestId: string;
          slug: string;
          unattributedReason: null;
      }
    | {
          kind: "unattributed";
          destinationHref: string;
          redirectRequestId: null;
          slug: string;
          unattributedReason: ShortLinkUnattributedReason;
      };

export type ShortLinkRedirectRequestEventInput = {
    slug: string;
    destinationUrl: string;
    redirectRequestId: string | null;
    unattributedReason?: ShortLinkUnattributedReason;
    attribution?: ShortLinkRequestAttribution;
    distinctId?: string;
    apiKey?: string;
};

export async function sendShortLinkRedirectRequestEvent(
    input: ShortLinkRedirectRequestEventInput,
    fetcher: IShortLinkRedirectEventFetcher = fetch,
) {
    const apiKey = input.apiKey ?? process.env.VITE_PUBLIC_POSTHOG_KEY;
    if (!apiKey) return;

    const hasDistinctId = Boolean(input.distinctId);
    const properties: ShortLinkRedirectEventProperties = {
        slug: input.slug,
        destination_url: shortLinkDestinationAnalyticsContext(
            input.destinationUrl,
        ).destinationUrl,
        ...(input.redirectRequestId
            ? { redirect_request_id: input.redirectRequestId }
            : {}),
        ...(input.unattributedReason
            ? { unattributed_reason: input.unattributedReason }
            : {}),
        ...(input.attribution?.aliasCampaign
            ? { alias_campaign: input.attribution.aliasCampaign }
            : {}),
        ...(input.attribution?.sourceReferrerHost
            ? { source_referrer_host: input.attribution.sourceReferrerHost }
            : {}),
        ...(input.attribution
            ? { source_channel: input.attribution.sourceChannel }
            : {}),
        ...(!hasDistinctId ? { $process_person_profile: false } : {}),
    };

    const body: ShortLinkRedirectEvent = {
        api_key: apiKey,
        event: "short_link_redirect",
        distinct_id: input.distinctId || REQUEST_ONLY_DISTINCT_ID,
        properties,
    };

    try {
        await fetcher(`${POSTHOG_API_HOST}/capture/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    } catch {}
}

function postHogDistinctIdFromCookie(cookie: string | undefined) {
    if (!cookie) return undefined;
    try {
        const parsed = postHogDistinctIdCookieSchema.safeParse(
            JSON.parse(decodeURIComponent(cookie)),
        );
        if (parsed.success) return parsed.data.distinct_id;
    } catch {}
    return undefined;
}

function makeShortLinkResolution({
    destinationUrl,
    slug,
    now,
    randomId,
}: {
    destinationUrl: string;
    slug: string;
    now?: number;
    randomId?: () => string;
}): ShortLinkResolution {
    const destination = createShortLinkDestination({
        destinationUrl,
        slug,
        ...(now === undefined ? {} : { now }),
        ...(randomId === undefined ? {} : { randomId }),
    });

    if (destination.kind === "bridged") {
        return {
            kind: "bridged",
            destinationHref: destination.href,
            redirectRequestId: destination.redirectRequestId,
            slug: destination.slug,
            unattributedReason: null,
        };
    }

    return {
        kind: "unattributed",
        destinationHref: destination.href,
        redirectRequestId: null,
        slug,
        unattributedReason: destination.reason,
    };
}

export { makeShortLinkResolution, postHogDistinctIdFromCookie };

export const resolveShortLink = createServerFn({ method: "GET" })
    .inputValidator((value: BoundaryUnknown) =>
        resolveShortLinkInputSchema.parse(value),
    )
    .handler(async ({ data }) => {
        const { setResponseHeader } =
            await import("@tanstack/react-start/server");
        setResponseHeader(
            "Cache-Control",
            SHORT_LINK_PRIVATE_NO_STORE_HEADERS["Cache-Control"],
        );

        const slug = data.caseSensitive
            ? data.slug.trim()
            : data.slug.toLowerCase().trim();
        const { getDb } = await import("@wags/db");
        const db = await getDb();

        const [link] = await db
            .select({ destinationUrl: shortLinks.destinationUrl })
            .from(shortLinks)
            .where(eq(shortLinks.slug, slug))
            .limit(1);

        if (!link) return null;

        const destination = makeShortLinkResolution({
            destinationUrl: link.destinationUrl,
            slug,
        });

        const { getCookie } = await import("@tanstack/react-start/server");
        const apiKey = process.env.VITE_PUBLIC_POSTHOG_KEY;
        const posthogCookie = apiKey
            ? getCookie(`ph_${apiKey}_posthog`)
            : undefined;
        const attribution: ShortLinkRequestAttribution | undefined =
            data.aliasCampaign || data.sourceReferrerHost || data.sourceChannel
                ? {
                      ...(data.aliasCampaign
                          ? { aliasCampaign: data.aliasCampaign }
                          : {}),
                      ...(data.sourceReferrerHost
                          ? { sourceReferrerHost: data.sourceReferrerHost }
                          : {}),
                      sourceChannel: data.sourceChannel ?? "unknown",
                  }
                : undefined;

        void sendShortLinkRedirectRequestEvent({
            slug,
            destinationUrl: link.destinationUrl,
            redirectRequestId: destination.redirectRequestId,
            ...(destination.kind === "unattributed"
                ? { unattributedReason: destination.unattributedReason }
                : {}),
            attribution,
            distinctId: postHogDistinctIdFromCookie(posthogCookie),
            apiKey,
        });

        return destination;
    });
